import React, { useState, useEffect } from 'react';
import { Moon, Sun, Download, Upload, LogOut, FileSpreadsheet, FileText, Building2, Sparkles, FileImage, Mail, FileUp, FileDown, Lock, Settings, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useBudget } from '@/contexts/BudgetContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { ScanPackModal } from '@/components/subscription/ScanPackModal';
import NotificationBell from '@/components/notifications/NotificationBell';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';

export const Header = ({ title, subtitle, onSettingsClick }) => {
  const { settings, toggleDarkMode, exportData, importData, reloadData } = useBudget();
  const { user, logout, authFetch } = useAuth();
  const subscription = useSubscription();
  const [selectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [showScanPackModal, setShowScanPackModal] = useState(false);
  
  const canUseAI = subscription.canScan();

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Import handlers
  const handleJsonImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = async (event) => {
          const result = await importData(event.target?.result);
          if (result) toast.success('Data imported!');
          else toast.error('Import failed');
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const handleCsvImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx,.xls';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await authFetch('/api/import/transactions', { method: 'POST', body: formData, headers: {} });
        const result = await response.json();
        if (response.ok) {
          toast.success(`Imported ${result.imported} transactions!`);
          await reloadData();
        } else {
          toast.error(result.detail || 'Import failed');
        }
      } catch (error) {
        toast.error('Import failed');
      }
    };
    input.click();
  };

  const handleBankImport = () => {
    window.dispatchEvent(new CustomEvent('openBankImport'));
  };

  const handlePdfImport = () => {
    window.dispatchEvent(new CustomEvent('openPdfImport'));
  };

  // Export handlers
  const handleCsvExport = async () => {
    try {
      const response = await authFetch('/api/export/transactions?format=csv');
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported!');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleExcelExport = async () => {
    try {
      const response = await authFetch('/api/export/transactions?format=excel');
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported!');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handlePdfReportExport = async () => {
    // AI-powered PDF generation uses a scan
    if (!canUseAI) {
      toast.error('AI scan limit reached. Purchase more scans to continue.');
      setShowScanPackModal(true);
      return;
    }
    
    try {
      await subscription.useScan();
      const response = await authFetch(`/api/export/report/pdf?month=${selectedMonth}`);
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `spending_report_${selectedMonth}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('PDF report downloaded!');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleChartExport = async (type) => {
    try {
      const url = type === 'spending' 
        ? `/api/export/chart/spending?month=${selectedMonth}`
        : '/api/export/chart/monthly?months=6';
      const response = await authFetch(url);
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = type === 'spending' ? `spending_chart_${selectedMonth}.png` : 'monthly_chart.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Chart downloaded!');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleEmailReport = async () => {
    // AI-powered email report uses a scan
    if (!canUseAI) {
      toast.error('AI scan limit reached. Purchase more scans to continue.');
      setShowScanPackModal(true);
      return;
    }
    
    try {
      await subscription.useScan();
      const response = await authFetch('/api/reports/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month: selectedMonth })
      });
      const result = await response.json();
      if (response.ok) {
        toast.success(`Report sent to ${user.email}!`);
      } else {
        toast.error(result.detail || 'Failed to send email');
      }
    } catch (error) {
      toast.error('Failed to send report');
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border/50">
      <div className="flex items-center justify-between px-4 py-3 max-w-lg mx-auto">
        <div className="flex items-center gap-3">
          {/* Logo */}
          <img 
            src="https://customer-assets.emergentagent.com/job_smarttally-3/artifacts/m32h86uo_download%20%283%29.png" 
            alt="SnapTally Logo" 
            className="h-10 w-auto"
          />
          <div>
            <h1 className="text-xl font-display font-bold text-foreground">{title}</h1>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          {/* Notification Bell */}
          <NotificationBell />
          
          {/* Import Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="import-menu-btn">
                <Upload className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="flex items-center gap-2">
                <Upload className="w-4 h-4" /> Import Data
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleBankImport} className="gap-2">
                <Building2 className="w-4 h-4 text-violet-500" />
                <span className="flex-1">Bank Statement</span>
                <Badge className="text-[9px] bg-violet-500/10 text-violet-500 border-0">AI</Badge>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handlePdfImport} className="gap-2">
                <FileUp className="w-4 h-4 text-red-500" />
                <span className="flex-1">PDF Document</span>
                <Badge className="text-[9px] bg-red-500/10 text-red-500 border-0">AI</Badge>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleCsvImport} className="gap-2">
                <FileSpreadsheet className="w-4 h-4 text-emerald-500" />
                CSV / Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleJsonImport} className="gap-2">
                <FileText className="w-4 h-4 text-blue-500" />
                JSON Backup
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Export Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="export-menu-btn">
                <Download className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="flex items-center gap-2">
                <Download className="w-4 h-4" /> Export Data
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleCsvExport} className="gap-2">
                <FileSpreadsheet className="w-4 h-4 text-emerald-500" />
                Transactions (CSV)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExcelExport} className="gap-2">
                <FileSpreadsheet className="w-4 h-4 text-blue-500" />
                Transactions (Excel)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportData} className="gap-2">
                <FileText className="w-4 h-4 text-amber-500" />
                Full Backup (JSON)
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground">Reports & Charts</DropdownMenuLabel>
              <DropdownMenuItem onClick={handlePdfReportExport} className="gap-2">
                <FileDown className="w-4 h-4 text-red-500" />
                Monthly Report (PDF)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleChartExport('spending')} className="gap-2">
                <FileImage className="w-4 h-4 text-violet-500" />
                Spending Chart (PNG)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleChartExport('monthly')} className="gap-2">
                <FileImage className="w-4 h-4 text-teal-500" />
                Monthly Chart (PNG)
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleEmailReport} className="gap-2">
                <Mail className="w-4 h-4 text-primary" />
                Email Report to Me
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Theme Toggle */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9" onClick={toggleDarkMode} data-testid="dark-mode-btn">
                  {settings.darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{settings.darkMode ? 'Light Mode' : 'Dark Mode'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full" data-testid="user-menu-btn">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-primary/10 text-primary text-xs">{getInitials(user?.name)}</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onSettingsClick} data-testid="settings-menu-btn">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'pricing' }))} data-testid="pricing-menu-btn">
                <Crown className="mr-2 h-4 w-4 text-amber-500" />
                <span className="flex-1">Upgrade Plan</span>
                <Badge variant="outline" className="text-[10px]">{subscription.tierName}</Badge>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive" data-testid="logout-btn">
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* Scan Pack Modal */}
      <ScanPackModal 
        open={showScanPackModal} 
        onOpenChange={setShowScanPackModal} 
      />
    </header>
  );
};
